import React from 'react';
import { TriangleAlert } from 'lucide-react';
import Seo, { faqLd, serviceLd } from '../../components/Seo';
import Reveal from '../../components/Reveal';
import {
  CompareTable, Faq, IconCards, Intro, ProductHero, QuoteSection, ScreenTour, SectionTitle, Steps, TrustStrip,
} from '../../components/product/ProductKit';
import { SIP_TRUNK as D } from '../../data/sipTrunk';
import { useSiteContent, withProductContent } from '../../content/siteContent';

const SIPTrunking = () => {
  const P = withProductContent(useSiteContent('sip-trunking'), D);
  return (
  <div className="min-h-screen bg-white text-[#0a1f2e]">
    <Seo
        title="SIP Trunking: Business SIP Trunks for Any PBX"
        description="VOIP2 SIP trunks: replace ISDN lines, keep your numbers, scale channels in minutes and stay online with built-in failover. Works with any PBX."
        path="/products/sip-trunking"
        breadcrumbs={[{ name: 'SIP Trunking', path: '/products/sip-trunking' }]}
        jsonLd={[serviceLd({ name: 'SIP Trunking', description: "VOIP2 SIP trunks: replace ISDN lines, keep your numbers, scale channels in minutes and stay online with built-in failover. Works with any PBX.", path: '/products/sip-trunking' }), faqLd(D.faqs)]}
      />

    <ProductHero
      {...P.hero}
      primary={{ label: 'Order online', href: '/sip-trunk/order' }}
      secondary={{ label: 'Get a quote', href: '#quote' }}
      media={(
        <>
          <div className="absolute inset-x-8 bottom-6 top-16 -z-10 rounded-[3rem] bg-[radial-gradient(ellipse_at_center,rgba(200,255,0,0.3),transparent_65%)]" />
          <img src={D.hero.image} alt="An office phone system connected through the VOIP2 cloud to the telephone network" className="w-full" />
        </>
      )}
    />

    <TrustStrip items={P.trust} />

    <Intro {...P.intro} />

    {/* Switch-off notice */}
    <section className="px-4 pb-24 sm:px-6 lg:px-8">
      <Reveal className="mx-auto flex max-w-7xl flex-col gap-6 rounded-3xl bg-[#0a1f2e] p-8 text-white md:flex-row md:items-center md:p-12">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#c8ff00] text-[#0a1f2e]">
          <TriangleAlert className="h-7 w-7" strokeWidth={1.75} />
        </span>
        <div className="flex-1">
          <h2 className="font-display text-2xl font-semibold md:text-3xl">{D.switchOff.title}</h2>
          <p className="mt-2 max-w-3xl text-gray-300">{D.switchOff.text}</p>
        </div>
        <a href="#quote" className="inline-flex shrink-0 items-center justify-center rounded-full bg-[#c8ff00] px-7 py-3.5 font-semibold text-[#0a1f2e] transition hover:brightness-95">
          Plan my migration
        </a>
      </Reveal>
    </section>

    <section id="platform" className="scroll-mt-20 bg-[#f5f7f9] px-4 py-24 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <SectionTitle title={D.platform.title} sub={D.platform.sub} />
        <ScreenTour screens={D.platform.screens} />
      </div>
    </section>

    <section className="px-4 py-24 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <SectionTitle title={D.benefits.title} />
        <IconCards items={D.benefits.items} />
      </div>
    </section>

    <section className="bg-[#f5f7f9] px-4 py-24 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl">
        <SectionTitle title={D.compare.title} center />
        <CompareTable head={D.compare.head} rows={D.compare.rows} />
      </div>
    </section>

    {/* Compatibility */}
    <section className="px-4 py-24 sm:px-6 lg:px-8">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionTitle title={D.compatibility.title} sub={D.compatibility.sub} />
        </div>
        <Reveal delay={120} className="grid grid-cols-2 gap-3 self-center sm:grid-cols-4 lg:col-span-7">
          {D.compatibility.systems.map((s) => (
            <div key={s} className="flex min-h-[88px] items-center justify-center rounded-2xl border border-gray-200 bg-white px-4 text-center font-display text-lg font-semibold">
              {s}
            </div>
          ))}
        </Reveal>
      </div>
    </section>

    <section className="bg-[#f5f7f9] px-4 py-24 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <SectionTitle title={D.steps.title} />
        <Steps steps={D.steps.items} />
      </div>
    </section>

    <Faq faqs={D.faqs} />

    <QuoteSection
      title="Get a SIP trunk quote"
      sub="Tell us about your phone system and call volumes. We will size the trunk and send a quote within one working day."
      service="SIP Trunking"
      contextLabel="How many channels do you need?"
      contextOptions={D.channelOptions}
      related={D.related}
    />
  </div>
  );
};

export default SIPTrunking;
